import * as plugins from './plugins.js';
import { VirtualStream } from './classes.virtualstream.js';

import { TypedHandler } from './classes.typedhandler.js';
import { TypedRequest } from './classes.typedrequest.js';
import { TypedResponseError } from './classes.typedresponseerror.js';
import {
  addGlobalHookSubscriber,
  clearGlobalHookRegistrations,
  createTypedRouterHookRegistrationState,
  dispatchTypedRequestHook,
  getLegacyGlobalHooks,
  mergeLegacyGlobalHooks,
  mergeTypedRouterHookRegistrations,
  replaceLegacyGlobalHooks,
  replaceTypedRouterHookRegistrations,
} from './internal.hooks.js';
import type {
  ITypedRouterHookProjectionOptions,
  ITypedRouterHooks,
} from './interfaces.hooks.js';

export {
  TYPED_REQUEST_REDACTED_PAYLOAD,
} from './interfaces.hooks.js';
export type {
  ITypedRequestLogEntry,
  ITypedRequestLogMetadata,
  ITypedRouterHookProjectionOptions,
  ITypedRouterHooks,
  TTypedRouterHookCallback,
} from './interfaces.hooks.js';

/**
 * Middleware function that runs before the handler.
 * Throw TypedResponseError to reject the request.
 */
export type TMiddlewareFunction<TReq extends Partial<plugins.typedRequestInterfaces.ITypedRequest> = plugins.typedRequestInterfaces.ITypedRequest> = (
  typedRequest: plugins.typedRequestInterfaces.ITypedRequest & TReq
) => Promise<void>;

export interface ITypedRouterRouteOptions {
  localRequest?: boolean;
  skipHooks?: boolean;
  /**
   * Server-owned transport data applied after network payload decoding. When
   * provided, caller-supplied localData is discarded completely.
   */
  trustedLocalData?: Record<string, unknown>;
}

/**
 * A typed router decides on which typed handler to call based on the method
 * specified in the typed request
 * This is thought for reusing the same url endpoint for different methods
 */
export class TypedRouter<TReqConstraint extends Partial<plugins.typedRequestInterfaces.ITypedRequest> = plugins.typedRequestInterfaces.ITypedRequest> {
  private static readonly internalErrorMessage = 'Internal server error';

  public static get globalHooks(): ITypedRouterHooks {
    return getLegacyGlobalHooks();
  }

  public static set globalHooks(value: ITypedRouterHooks) {
    replaceLegacyGlobalHooks(value);
  }

  /**
   * Set global hooks for monitoring all TypedRequest traffic
   * Hooks are shared across all bundles via globalThis
   */
  public static setGlobalHooks(
    hooks: ITypedRouterHooks,
    projectionOptions?: ITypedRouterHookProjectionOptions
  ): void {
    mergeLegacyGlobalHooks(hooks, projectionOptions);
  }

  /**
   * Add an independently projected global hook subscriber.
   */
  public static addGlobalHooks(
    hooks: ITypedRouterHooks,
    projectionOptions?: ITypedRouterHookProjectionOptions
  ): () => void {
    return addGlobalHookSubscriber(hooks, projectionOptions);
  }

  /**
   * Clear all global hooks
   */
  public static clearGlobalHooks(): void {
    clearGlobalHookRegistrations();
  }

  private readonly hookRegistrationState =
    createTypedRouterHookRegistrationState();

  public get hooks(): ITypedRouterHooks {
    return this.hookRegistrationState.hooks;
  }

  public set hooks(value: ITypedRouterHooks) {
    replaceTypedRouterHookRegistrations(this.hookRegistrationState, value);
  }

  /**
   * Set instance-level hooks for monitoring traffic through this router
   */
  public setHooks(
    hooks: ITypedRouterHooks,
    projectionOptions?: ITypedRouterHookProjectionOptions
  ): void {
    mergeTypedRouterHookRegistrations(
      this.hookRegistrationState,
      hooks,
      projectionOptions
    );
  }

  /**
   * Helper to call both global and instance hooks
   */
  private callHook(
    hookName: keyof ITypedRouterHooks,
    source: Parameters<typeof dispatchTypedRequestHook>[1]
  ): void {
    dispatchTypedRequestHook(hookName, source, this.hookRegistrationState);
  }

  private setErrorFromCaughtError(
    typedRequestArg: plugins.typedRequestInterfaces.ITypedRequest,
    errorArg: unknown,
    contextArg: string,
  ): void {
    if (errorArg instanceof TypedResponseError) {
      typedRequestArg.error = { text: errorArg.errorText, data: errorArg.errorData ?? {} };
      return;
    }

    console.error(`TypedRequest ${contextArg} failed`);
    typedRequestArg.error = { text: TypedRouter.internalErrorMessage, data: {} };
  }

  public routerMap = new plugins.lik.ObjectMap<TypedRouter<any>>();
  /**
   * One-way fallback routers. A fallback can serve this router without being
   * able to route back into it.
   */
  private fallbackRouterMap = new plugins.lik.ObjectMap<TypedRouter<any>>();

  /**
   * Validation-only inbound links. They never participate in routing; they
   * let a composed parent reject collisions introduced by later mutations.
   */
  private fallbackParentRouters = new Set<TypedRouter<any>>();
  public handlerMap = new plugins.lik.ObjectMap<
    TypedHandler<any & plugins.typedRequestInterfaces.ITypedRequest>
  >();
  public registeredVirtualStreams = new plugins.lik.ObjectMap<VirtualStream<any>>();

  public getRegisteredVirtualStreamById(
    streamIdArg: string
  ): VirtualStream<any> | undefined {
    return this.registeredVirtualStreams.findSync(
      (virtualStreamArg) => virtualStreamArg.streamId === streamIdArg
    );
  }

  public registerVirtualStream(
    virtualStreamArg: VirtualStream<any>
  ): VirtualStream<any> {
    const existingStream = this.getRegisteredVirtualStreamById(
      virtualStreamArg.streamId
    );
    if (existingStream) {
      return existingStream;
    }
    this.registeredVirtualStreams.add(virtualStreamArg);
    return virtualStreamArg;
  }

  public unregisterVirtualStream(
    virtualStreamArg: VirtualStream<any>
  ): void {
    this.registeredVirtualStreams.remove(virtualStreamArg);
  }

  // Middleware chain
  private middlewares: TMiddlewareFunction<TReqConstraint>[] = [];

  /**
   * Adds a middleware function that runs before the handler on this router.
   * Throw TypedResponseError to reject the request.
   */
  public addMiddleware(fn: TMiddlewareFunction<TReqConstraint>): void {
    this.middlewares.push(fn);
  }

  public fireEventInterestMap = new plugins.lik.InterestMap<
    string,
    plugins.typedRequestInterfaces.ITypedRequest
  >((correlationId: string) => correlationId);

  /**
   * adds the handler to the routing map
   * @param typedHandlerArg
   */
  public addTypedHandler<T extends plugins.typedRequestInterfaces.ITypedRequest & TReqConstraint>(
    typedHandlerArg: TypedHandler<T>
  ) {
    // lets check for deduplication
    const existingTypedHandler = this.getTypedHandlerForMethod(typedHandlerArg.method);
    if (existingTypedHandler) {
      throw new Error(
        `a TypedHandler for ${typedHandlerArg.method} alredy exists! Can't add another one.`
      );
    }

    this.handlerMap.add(typedHandlerArg);
    try {
      this.validateAffectedCompositionRoots();
    } catch (error) {
      this.handlerMap.remove(typedHandlerArg);
      throw error;
    }
  }

  /**
   * adds another sub typedRouter
   * @param typedRequest
   */
  public addTypedRouter(typedRouterArg: TypedRouter<any>) {
    if (typedRouterArg === this) {
      throw new Error('A TypedRouter cannot be composed with itself.');
    }
    const routerExists = this.routerMap.findSync((routerArg) => routerArg === typedRouterArg);
    if (!routerExists) {
      const thisCanReachOther = this.canReachRouter(typedRouterArg);
      const otherCanReachThis = typedRouterArg.canReachRouter(this);
      if (thisCanReachOther !== otherCanReachThis) {
        throw new Error(
          'Cannot add a bidirectional TypedRouter edge across an existing composition boundary.',
        );
      }
      this.routerMap.add(typedRouterArg);
      typedRouterArg.routerMap.add(this);
      try {
        this.validateAffectedCompositionRoots();
      } catch (error) {
        this.routerMap.remove(typedRouterArg);
        typedRouterArg.routerMap.remove(this);
        throw error;
      }
    }
  }

  /**
   * Adds a router as a one-way fallback.
   *
   * Requests entering this router can reach the fallback router, while
   * requests entering the fallback router cannot reach this router. This is
   * useful for composing internal protocol handlers into multiple isolated
   * application routers.
   */
  public addFallbackRouter(typedRouterArg: TypedRouter<any>): () => void {
    if (typedRouterArg === this) {
      throw new Error('A TypedRouter cannot use itself as a fallback.');
    }
    if (this.fallbackRouterMap.findSync((routerArg) => routerArg === typedRouterArg)) {
      return () => {};
    }
    if (typedRouterArg.canReachRouter(this)) {
      throw new Error('Adding this fallback would create a TypedRouter composition cycle.');
    }

    this.fallbackRouterMap.add(typedRouterArg);
    typedRouterArg.fallbackParentRouters.add(this);
    try {
      this.validateAffectedCompositionRoots();
    } catch (error) {
      this.fallbackRouterMap.remove(typedRouterArg);
      typedRouterArg.fallbackParentRouters.delete(this);
      throw error;
    }

    let detached = false;
    return () => {
      if (detached) {
        return;
      }
      detached = true;
      this.fallbackRouterMap.remove(typedRouterArg);
      typedRouterArg.fallbackParentRouters.delete(this);
    };
  }

  /**
   * Returns the unique method names reachable from this router.
   */
  public getMethodNames(): string[] {
    return [...this.collectReachableHandlers().keys()].sort();
  }

  private canReachRouter(
    targetRouterArg: TypedRouter<any>,
    checkedRoutersArg = new Set<TypedRouter<any>>(),
  ): boolean {
    if (this === targetRouterArg) {
      return true;
    }
    if (checkedRoutersArg.has(this)) {
      return false;
    }
    checkedRoutersArg.add(this);
    for (const router of [
      ...this.routerMap.getArray(),
      ...this.fallbackRouterMap.getArray(),
    ]) {
      if (router.canReachRouter(targetRouterArg, checkedRoutersArg)) {
        return true;
      }
    }
    return false;
  }

  private collectReachableHandlers(
    checkedRoutersArg = new Set<TypedRouter<any>>(),
    ownersByMethodArg = new Map<string, Set<TypedRouter<any>>>(),
  ): Map<string, Set<TypedRouter<any>>> {
    if (checkedRoutersArg.has(this)) {
      return ownersByMethodArg;
    }
    checkedRoutersArg.add(this);

    for (const handler of this.handlerMap.getArray()) {
      let methodOwners = ownersByMethodArg.get(handler.method);
      if (!methodOwners) {
        methodOwners = new Set();
        ownersByMethodArg.set(handler.method, methodOwners);
      }
      methodOwners.add(this);
    }
    for (const router of [
      ...this.routerMap.getArray(),
      ...this.fallbackRouterMap.getArray(),
    ]) {
      router.collectReachableHandlers(checkedRoutersArg, ownersByMethodArg);
    }
    return ownersByMethodArg;
  }

  private getAffectedCompositionRoots(): Set<TypedRouter<any>> {
    const affectedRouters = new Set<TypedRouter<any>>();
    const pendingRouters: TypedRouter<any>[] = [this];
    while (pendingRouters.length > 0) {
      const router = pendingRouters.pop()!;
      if (affectedRouters.has(router)) {
        continue;
      }
      affectedRouters.add(router);
      pendingRouters.push(
        ...router.routerMap.getArray(),
        ...router.fallbackParentRouters,
      );
    }
    return affectedRouters;
  }

  private validateAffectedCompositionRoots(): void {
    for (const rootRouter of this.getAffectedCompositionRoots()) {
      for (const [method, owners] of rootRouter.collectReachableHandlers()) {
        if (owners.size > 1) {
          throw new Error(
            `TypedRouter composition exposes more than one handler for method "${method}".`,
          );
        }
      }
    }
  }

  public checkForTypedHandler(methodArg: string): boolean {
    return !!this.getTypedHandlerForMethod(methodArg);
  }

  /**
   * gets a typed Router from the router chain, upstream and downstream
   * @param methodArg
   * @param checkUpstreamRouter
   */
  public getTypedHandlerForMethod(
    methodArg: string,
    checkedRouters: TypedRouter<any>[] = []
  ): TypedHandler<any> {
    checkedRouters.push(this);

    let typedHandler: TypedHandler<any>;

    typedHandler = this.handlerMap.findSync((handler) => {
      return handler.method === methodArg;
    });

    if (!typedHandler) {
      this.routerMap.getArray().forEach((typedRouterArg) => {
        if (!typedHandler && !checkedRouters.includes(typedRouterArg)) {
          typedHandler = typedRouterArg.getTypedHandlerForMethod(methodArg, checkedRouters);
        }
      });
    }

    if (!typedHandler) {
      this.fallbackRouterMap.getArray().forEach((typedRouterArg) => {
        if (!typedHandler && !checkedRouters.includes(typedRouterArg)) {
          typedHandler = typedRouterArg.getTypedHandlerForMethod(methodArg, checkedRouters);
        }
      });
    }

    return typedHandler;
  }

  /**
   * Finds the handler AND its owning router for a given method.
   * Needed to know which router's middleware to run.
   */
  public getTypedHandlerAndRouter(
    methodArg: string,
    checkedRouters: TypedRouter<any>[] = []
  ): { handler: TypedHandler<any>; router: TypedRouter<any> } | null {
    checkedRouters.push(this);
    const handler = this.handlerMap.findSync((h) => h.method === methodArg);
    if (handler) return { handler, router: this };
    for (const child of this.routerMap.getArray()) {
      if (!checkedRouters.includes(child)) {
        const result = child.getTypedHandlerAndRouter(methodArg, checkedRouters);
        if (result) return result;
      }
    }
    for (const fallback of this.fallbackRouterMap.getArray()) {
      if (!checkedRouters.includes(fallback)) {
        const result = fallback.getTypedHandlerAndRouter(methodArg, checkedRouters);
        if (result) return result;
      }
    }
    return null;
  }

  /**
   * Options for routeAndAddResponse
   */
  public static defaultRouteOptions = {
    localRequest: false,
    skipHooks: false,
  };

  /**
   * if typedrequest object has correlation.phase === 'request' -> routes a typed request object to a handler
   * if typedrequest object has correlation.phase === 'response' -> routes a typed request object to request fire event
   * @param typedRequestArg
   * @param optionsArg - Options object with:
   *   - localRequest: treat as local request (default: false)
   *   - skipHooks: skip calling hooks for this routing (default: false, use for broadcast-received messages)
   */
  public async routeAndAddResponse<
    T extends plugins.typedRequestInterfaces.ITypedRequest = plugins.typedRequestInterfaces.ITypedRequest
  >(typedRequestArg: T, optionsArg: ITypedRouterRouteOptions = {}): Promise<T> {
    const options = { ...TypedRouter.defaultRouteOptions, ...optionsArg };
    // decoding first
    typedRequestArg = VirtualStream.decodePayloadFromNetwork(typedRequestArg, {
      typedrouter: this,
    });

    // localdata second
    typedRequestArg.localData = options.trustedLocalData
      ? { ...options.trustedLocalData }
      : typedRequestArg.localData || {};
    typedRequestArg.localData.firstTypedrouter = this;

    // lets do stream processing
    if (typedRequestArg.method === '##VirtualStream##') {
      const result: any = await this.handleStreamTypedRequest(typedRequestArg as plugins.typedRequestInterfaces.IStreamRequest);
      result.localData = null;
      return result as T;
    }

    // lets do normal routing
    if (typedRequestArg?.correlation?.phase === 'request' || options.localRequest) {
      const requestStartTime = Date.now();

      // Hook: incoming request (skip if routing broadcast-received messages)
      if (!options.skipHooks) {
        this.callHook('onIncomingRequest', {
          correlationId: typedRequestArg.correlation?.id || 'unknown',
          method: typedRequestArg.method,
          direction: 'incoming',
          phase: 'request',
          timestamp: requestStartTime,
          payload: typedRequestArg.request,
        });
      }

      const result = this.getTypedHandlerAndRouter(typedRequestArg.method);

      if (!result) {
        console.log(`Cannot find handler for methodname ${typedRequestArg.method}`);
        typedRequestArg.error = {
          text: 'There is no available method for this call on the server side',
          data: {},
        };
        typedRequestArg.correlation!.phase = 'response';

        // encode again before handing back
        typedRequestArg.localData = undefined;
        typedRequestArg = VirtualStream.encodePayloadForNetwork(typedRequestArg, {
          typedrouter: this,
        });

        // Hook: outgoing response (error - no handler)
        if (!options.skipHooks) {
          this.callHook('onOutgoingResponse', {
            correlationId: typedRequestArg.correlation?.id || 'unknown',
            method: typedRequestArg.method,
            direction: 'outgoing',
            phase: 'response',
            timestamp: Date.now(),
            durationMs: Date.now() - requestStartTime,
            payload: typedRequestArg.response,
            errorText: typedRequestArg.error?.text,
          });
        }

        return typedRequestArg;
      }

      const { handler: typedHandler, router: owningRouter } = result;

      // Run owning router's middleware chain
      if (owningRouter.middlewares.length > 0) {
        try {
          for (const mw of owningRouter.middlewares) {
            await mw(typedRequestArg as any);
          }
        } catch (e) {
          this.setErrorFromCaughtError(typedRequestArg, e, `middleware for method '${typedRequestArg.method}'`);
          typedRequestArg.correlation!.phase = 'response';
          typedRequestArg.localData = undefined;
          typedRequestArg = VirtualStream.encodePayloadForNetwork(typedRequestArg, {
            typedrouter: this,
          });

          if (!options.skipHooks) {
            this.callHook('onOutgoingResponse', {
              correlationId: typedRequestArg.correlation?.id || 'unknown',
              method: typedRequestArg.method,
              direction: 'outgoing',
              phase: 'response',
              timestamp: Date.now(),
              durationMs: Date.now() - requestStartTime,
              payload: typedRequestArg.response,
              errorText: typedRequestArg.error?.text,
            });
          }

          return typedRequestArg;
        }
      }

      typedRequestArg = await typedHandler.addResponse(typedRequestArg);
      typedRequestArg.localData = undefined;
      // encode again before handing back
      typedRequestArg = VirtualStream.encodePayloadForNetwork(typedRequestArg, {
        typedrouter: this,
      });

      // Hook: outgoing response (success)
      if (!options.skipHooks) {
        this.callHook('onOutgoingResponse', {
          correlationId: typedRequestArg.correlation?.id || 'unknown',
          method: typedRequestArg.method,
          direction: 'outgoing',
          phase: 'response',
          timestamp: Date.now(),
          durationMs: Date.now() - requestStartTime,
          payload: typedRequestArg.response,
          errorText: typedRequestArg.error?.text,
        });
      }

      return typedRequestArg;
    } else if (typedRequestArg?.correlation?.phase === 'response') {
      // Hook: incoming response
      if (!options.skipHooks) {
        this.callHook('onIncomingResponse', {
          correlationId: typedRequestArg.correlation?.id || 'unknown',
          method: typedRequestArg.method,
          direction: 'incoming',
          phase: 'response',
          timestamp: Date.now(),
          payload: typedRequestArg.response,
          errorText: typedRequestArg.error?.text,
        });
      }

      this.fireEventInterestMap
        .findInterest(typedRequestArg.correlation.id)
        ?.fullfillInterest(typedRequestArg);
      return null as any;
    } else {
      console.log('TypedRouter received a malformed request DTO');
      return null as any;
    }
  }

  /**
   * handle streaming
   * @param streamTrArg 
   */
  public async handleStreamTypedRequest(streamTrArg: plugins.typedRequestInterfaces.IStreamRequest) {
    const relevantVirtualStream = this.getRegisteredVirtualStreamById(
      streamTrArg.request.streamId
    );
    if (!relevantVirtualStream) {
      streamTrArg.response = {
        streamId: streamTrArg.request.streamId,
        cycleId: streamTrArg.request.cycleId,
        cycle: 'response',
        mainPurpose: 'end',
        keepAlive: false,
        next: false,
        backpressure: false,
      };
      return streamTrArg;
    }
    return relevantVirtualStream.handleStreamTr(streamTrArg);
  }
}
